import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { 
  UserPlus, 
  Send, 
  Upload, 
  Smartphone, 
  Users, 
  CheckCircle,
  XCircle,
  Download,
  Share
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useToast } from '../../hooks/use-toast';

const FriendInvitation = ({ userRole = 'customer' }) => {
  const [invitations, setInvitations] = useState([]);
  const [bulkInvite, setBulkInvite] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [importedContacts, setImportedContacts] = useState([]);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteStats, setInviteStats] = useState({
    sent: 0,
    pending: 0,
    accepted: 0
  });

  const { user } = useAuth();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();

  // Backend invitation status -> UI status buckets (sent/delivered/opened => pending).
  const normStatus = (s) => {
    if (s === 'accepted') return 'accepted';
    if (s === 'expired' || s === 'declined') return 'expired';
    return 'pending';
  };

  // Load invitation history on mount
  useEffect(() => {
    loadInvitationHistory();
    loadInviteStats();
  }, []);

  const loadInvitationHistory = async () => {
    try {
      // Real, persisted invitations for the current user (durable in MongoDB, owner-scoped server-side).
      const response = await makeAuthenticatedRequest('/api/v1/contacts/invitations', { method: 'GET' });
      if (!response.ok) {
        // Backend unavailable for this session — honest empty state, never fabricated data.
        if (response.status >= 500) { setInvitations([]); return; }
        throw new Error(`Failed to load invitations (${response.status})`);
      }
      const data = await response.json();
      const raw = Array.isArray(data?.invitations) ? data.invitations : [];
      setInvitations(raw.map((inv) => ({
        id: inv.id,
        name: inv.target_email || inv.target_phone_number || 'Invitation',
        email: inv.target_email || '',
        phone: inv.target_phone_number || '',
        status: normStatus(inv.status),
        sent_at: inv.sent_at || inv.created_at || null,
      })));
    } catch (error) {
      console.error('Error loading invitation history:', error);
      setInvitations([]);
    }
  };

  const loadInviteStats = async () => {
    try {
      // Real referral stats computed from persisted invitation records.
      const response = await makeAuthenticatedRequest('/api/v1/contacts/referral-stats', { method: 'GET' });
      if (!response.ok) {
        if (response.status >= 500) { setInviteStats({ sent: 0, pending: 0, accepted: 0 }); return; }
        throw new Error(`Failed to load invite stats (${response.status})`);
      }
      const data = await response.json();
      setInviteStats({
        sent: data?.total_referrals || 0,
        pending: data?.pending_referrals || 0,
        accepted: data?.successful_referrals || 0,
      });
    } catch (error) {
      console.error('Error loading invite stats:', error);
      setInviteStats({ sent: 0, pending: 0, accepted: 0 });
    }
  };

  const parseContactsInput = (input) => {
    const lines = input.split('\n').filter(line => line.trim());
    const contacts = [];
    
    lines.forEach((line, index) => {
      try {
        // Support multiple formats:
        // 1. email
        // 2. name,email
        // 3. name,email,phone
        // 4. email,phone
        
        const parts = line.split(',').map(p => p.trim());
        
        if (parts.length === 1) {
          // Just email
          const email = parts[0];
          if (email.includes('@')) {
            contacts.push({
              id: `import_${index}`,
              email,
              name: email.split('@')[0],
              phone: ''
            });
          }
        } else if (parts.length >= 2) {
          let name = '', email = '', phone = '';
          
          // Detect which parts are email/phone
          parts.forEach(part => {
            if (part.includes('@')) {
              email = part;
            } else if (part.startsWith('+') || /^\d+$/.test(part.replace(/[\s\-\(\)]/g, ''))) {
              phone = part;
            } else {
              name = name ? `${name} ${part}` : part;
            }
          });
          
          if (email || phone) {
            contacts.push({
              id: `import_${index}`,
              name: name || email?.split('@')[0] || 'Contact',
              email: email || '',
              phone: phone || ''
            });
          }
        }
      } catch (error) {
        console.warn(`Error parsing line ${index + 1}: ${line}`, error);
      }
    });
    
    return contacts;
  };

  const handleBulkInvite = async () => {
    if (!bulkInvite.trim()) {
      setError('Please enter contact information');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const contacts = parseContactsInput(bulkInvite);

      if (contacts.length === 0) {
        throw new Error('No valid contacts found. Please check the format.');
      }

      // Invitations are delivered by SMS, so a phone number is required. Contacts with only an
      // email are reported as skipped (honest) rather than silently "sent".
      const withPhone = contacts.filter((c) => c.phone && c.phone.trim());
      const skipped = contacts.length - withPhone.length;
      if (withPhone.length === 0) {
        throw new Error('Invitations are sent by SMS — please include a phone number for each contact.');
      }

      const inviteMessage = customMessage || getDefaultMessage();

      // Real bulk invite — persisted in MongoDB via the backend.
      const response = await makeAuthenticatedRequest('/api/v1/contacts/bulk-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitations: withPhone.slice(0, 50).map((c) => ({ phone_number: c.phone.trim() })),
          invitation_type: 'customer',
          message: inviteMessage,
        }),
      });

      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(`Failed to send invitations (${response.status})${detail ? `: ${detail}` : ''}`);
      }

      const result = await response.json();
      const successful = result?.successful ?? 0;
      const failed = result?.failed ?? 0;

      setBulkInvite('');
      setCustomMessage('');
      setShowBulkInvite(false);
      setImportedContacts([]);

      // Re-pull persisted truth instead of optimistically fabricating rows.
      await loadInvitationHistory();
      await loadInviteStats();

      toast({
        title: successful > 0 ? 'Invitations Sent' : 'No Invitations Sent',
        description: `${successful} sent${failed ? `, ${failed} failed` : ''}${skipped ? `, ${skipped} skipped (no phone number)` : ''}.`,
        variant: successful > 0 ? undefined : 'destructive',
      });

    } catch (error) {
      setError(error.message);
      toast({
        title: "Invitation Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceContactImport = async () => {
    // Uses the Web Contact Picker API (navigator.contacts) where the browser supports it
    // (Chrome/Android over HTTPS). No fabricated contacts — if unsupported, fall back to manual entry.
    try {
      setIsLoading(true);

      const supported =
        typeof navigator !== 'undefined' &&
        'contacts' in navigator &&
        navigator.contacts &&
        typeof navigator.contacts.select === 'function';

      if (!supported) {
        setShowBulkInvite(true);
        toast({
          title: "Device Import Not Supported",
          description: "This browser can't read device contacts. Please enter contacts manually below.",
        });
        return;
      }

      const selected = await navigator.contacts.select(['name', 'email', 'tel'], { multiple: true });

      if (!selected || selected.length === 0) {
        toast({ title: "No Contacts Selected", description: "No contacts were imported." });
        return;
      }

      const parsed = selected.map((c) => ({
        name: (c.name && c.name[0]) || '',
        email: (c.email && c.email[0]) || '',
        phone: (c.tel && c.tel[0]) || '',
      }));

      setImportedContacts(parsed);
      setBulkInvite(parsed.map((c) => `${c.name},${c.email},${c.phone}`).join('\n'));
      setShowBulkInvite(true);

      toast({
        title: "Contacts Imported",
        description: `Imported ${parsed.length} contact(s) from your device.`,
      });

    } catch (error) {
      // AbortError = user dismissed the picker; treat as a no-op, not a failure.
      if (error && error.name === 'AbortError') return;
      toast({
        title: "Import Failed",
        description: "Could not import contacts from your device.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultMessage = () => {
    const messages = {
      customer: `Hi! I've been using Servisfy for customer service and it's amazing! You can get instant support from businesses through voice, chat, and video calls. Download it here: `,
      admin: `Hey! I'm using Servisfy to manage our customer service operations. It's a game-changer for business communications. Check it out: `,
      manager: `Hi! Our team uses Servisfy for streamlined customer management. It integrates everything - calls, chats, tickets, and analytics. Take a look: `
    };
    
    return messages[userRole] || messages.customer;
  };

  const getInviteLinks = () => {
    return {
      ios: 'https://apps.apple.com/app/servisfy',
      android: 'https://play.google.com/store/apps/details?id=com.servisfy.app',
      web: 'https://servisfy.com/download'
    };
  };

  const handleShareLink = async (platform) => {
    const links = getInviteLinks();
    const message = `${getDefaultMessage()}${links[platform]}`;
    
    if (navigator.share && platform === 'native') {
      try {
        await navigator.share({
          title: 'Join me on Servisfy',
          text: message,
          url: links.web
        });
      } catch (error) {
        console.log('Share cancelled or failed:', error);
      }
    } else {
      // Fallback to clipboard
      await navigator.clipboard.writeText(message);
      toast({
        title: "Link Copied",
        description: "Invitation link copied to clipboard!",
      });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInvitations = invitations.filter((inv) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      inv.name?.toLowerCase().includes(q) ||
      inv.email?.toLowerCase().includes(q) ||
      inv.phone?.includes(q) ||
      inv.status?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <UserPlus className="w-5 h-5 mr-2" />
              Invite Friends to Servisfy
            </span>
            {user && (
              <Badge variant="outline" className="text-xs font-normal">
                Inviting as {user.displayName || user.email || user.phoneNumber || 'User'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Send className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{inviteStats.sent}</p>
              <p className="text-sm text-blue-600">Invitations Sent</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <Users className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-700">{inviteStats.pending}</p>
              <p className="text-sm text-yellow-600">Pending</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{inviteStats.accepted}</p>
              <p className="text-sm text-green-600">Joined</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Quick Share Links */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Share</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Button 
              onClick={() => handleShareLink('ios')}
              variant="outline"
              className="flex items-center"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              iOS App Store
            </Button>
            <Button 
              onClick={() => handleShareLink('android')}
              variant="outline"
              className="flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Google Play
            </Button>
            <Button 
              onClick={() => handleShareLink('web')}
              variant="outline"
              className="flex items-center"
            >
              <Share className="w-4 h-4 mr-2" />
              Web App
            </Button>
            {navigator.share && (
              <Button 
                onClick={() => handleShareLink('native')}
                variant="outline"
                className="flex items-center"
              >
                <Share className="w-4 h-4 mr-2" />
                Native Share
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Bulk Invitation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              Bulk Invitation
              {importedContacts.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {importedContacts.length} imported
                </Badge>
              )}
            </span>
            <div className="space-x-2">
              <Button 
                onClick={handleDeviceContactImport}
                variant="outline"
                size="sm"
                disabled={isLoading}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Contacts
              </Button>
              <Button 
                onClick={() => setShowBulkInvite(!showBulkInvite)}
                size="sm"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Manual Entry
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        
        {showBulkInvite && (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bulkContacts">Contact Information</Label>
              <Textarea
                id="bulkContacts"
                placeholder="One contact per line. Include a phone number (required for SMS invite); email is optional."
                value={bulkInvite}
                onChange={(e) => setBulkInvite(e.target.value)}
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Supported formats: email only, name+email, name+email+phone, email+phone
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="customMessage">Custom Message (Optional)</Label>
              <Textarea
                id="customMessage"
                placeholder={`Default: ${getDefaultMessage()}`}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500">
                {customMessage.length}/500 characters
              </p>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            
            <Button 
              onClick={handleBulkInvite}
              disabled={isLoading || !bulkInvite.trim()}
              className="w-full"
            >
              {isLoading ? 'Sending Invitations...' : 'Send Invitations'}
            </Button>
          </CardContent>
        )}
      </Card>

      <Separator />

      {/* Invitation History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Search by name, email, phone, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {filteredInvitations.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {searchQuery.trim()
                ? 'No invitations match your search.'
                : 'No invitations sent yet. Start by inviting your friends above!'}
            </p>
          ) : (
            <div className="space-y-3">
              {filteredInvitations.slice(0, 10).map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium">{invitation.name}</h4>
                      <Badge className={getStatusBadgeColor(invitation.status)}>
                        {invitation.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{invitation.email}</p>
                    {invitation.phone && (
                      <p className="text-sm text-gray-500">{invitation.phone}</p>
                    )}
                    {invitation.sent_at && (
                      <p className="text-xs text-gray-400">
                        Sent: {new Date(invitation.sent_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {invitation.status === 'accepted' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : invitation.status === 'pending' ? (
                      <Users className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FriendInvitation;